import {
  FdeCdfSpecification,
  useCdfRawValues2DArray,
} from 'features/dataLayers'
import { getInspectionDate } from 'features/fde'
import CalculateCascadedScenariosWorker from 'features/fde/assetHealthPlanning/simulation/calculateCascadedScenarios.worker?worker'
import { isEqual, isNil } from 'lodash'
import { useCallback, useMemo, useRef } from 'react'
import useSWRImmutable from 'swr/immutable'
import {
  AhpScenario,
  AssetHealthDataFrame,
  ForecastComponentConfig,
  GeckoAssetModel,
  GeckoComponentDataFrame,
  GeckoInspection,
  GeckoMeasurementUnit,
  GeckoTMinMethod,
  isInspectionThicknessCDF,
} from 'types'
import { DATE_FORMAT } from 'utils/fde/constants'

import { CalculateCascadeOfScenariosWorkerProps } from './calculateCascadedScenarios.worker'

export function useCalculateAssetHealthPlan({
  baseThicknessCDF,
  forecastComponentConfig,
  assetHealthPlanScenarios,
  selectedInspection,
  assetModel,
  thicknessValues2dArray,
}: {
  baseThicknessCDF: GeckoComponentDataFrame | undefined
  forecastComponentConfig: ForecastComponentConfig | undefined
  assetHealthPlanScenarios: AhpScenario[]
  selectedInspection: GeckoInspection | undefined
  assetModel: GeckoAssetModel | undefined
  thicknessValues2dArray?: number[][] | readonly (readonly number[])[]
}): {
  dataFrames: ReadonlyArray<AssetHealthDataFrame> | undefined
  isLoading: boolean
  hasEverLoaded: boolean
} {
  // ------------------------------------ Fetch Raw Thickness Values ------------------------------------
  // Ensure that the baseThicknessCDF is a thickness CDF
  if (!isNil(baseThicknessCDF) && !isInspectionThicknessCDF(baseThicknessCDF)) {
    throw new Error(
      `Invalid base thickness CDF used in remaining life CDF generation: ${baseThicknessCDF.id}`
    )
  }

  const hasEverLoaded = useRef(false)

  const {
    array: baseThicknessValues = thicknessValues2dArray,
    isLoading: baseThicknessValuesAreLoading = false,
  } = useCdfRawValues2DArray(
    thicknessValues2dArray ? undefined : baseThicknessCDF
  )

  const assetModelJson = assetModel?.raw

  function getCdfSpecificationMap() {
    return {
      [GeckoTMinMethod.TMinByShellCourse.id]:
        FdeCdfSpecification.RlByShellCourse,
      [GeckoTMinMethod.TMinByApi653.id]: FdeCdfSpecification.RlByApi653,
      [GeckoTMinMethod.TMinByApi570.id]: FdeCdfSpecification.RlByApi570,
    }
  }

  // A function that initializes and calls a Web-Worker to calculate the remaining life values
  const calculateDataInWebWorker = useCallback(
    () =>
      Promise.resolve(
        new Promise<{
          calcParams: CalculateCascadeOfScenariosWorkerProps
          dataFrames: AssetHealthDataFrame[]
        }>((resolve, reject) => {
          try {
            // Assert we've got a valid base thickness CDF
            if (isNil(baseThicknessCDF)) {
              reject()
              return
            }

            // Assert that the API calls we depend on have resolved
            if (baseThicknessValuesAreLoading) {
              reject()
              return
            }

            // Assert that the API calls returned valid data
            if (
              isNil(assetModelJson) ||
              isNil(baseThicknessValues) ||
              isNil(baseThicknessCDF?.tags.inspection)
            ) {
              reject()
              return
            }

            // Assemble the values needed for the remaining life calc
            // -- CDF data values
            const baseInspectionDate = getInspectionDate({
              inspectionSlug: baseThicknessCDF.tags.inspection,
              inspection: selectedInspection,
            })
            if (isNil(baseInspectionDate)) {
              reject()
              return
            }

            const binWidthFt =
              baseThicknessCDF.data.binning.bin_size.width.convertTo(
                GeckoMeasurementUnit.Feet
              ).num
            const binHeightFt =
              baseThicknessCDF.data.binning.bin_size.height.convertTo(
                GeckoMeasurementUnit.Feet
              ).num
            const xGeometryStartFt =
              baseThicknessCDF.data.geometry.start.x.convertTo(
                GeckoMeasurementUnit.Feet
              ).num
            const yGeometryStartFt =
              baseThicknessCDF.data.geometry.start.y.convertTo(
                GeckoMeasurementUnit.Feet
              ).num

            // our tmin method should be the same across all actions
            const tmin = assetHealthPlanScenarios?.[0]?.tminMethod
            const tminMethod = GeckoTMinMethod.GetOrCreate(tmin?.id)
            const componentModel = assetModel?.data.components.get(
              baseThicknessCDF.tags.component
            )
            const cdfSpecificationMap = getCdfSpecificationMap()
            let selectedCdfSpec: FdeCdfSpecification | undefined =
              tminMethod && cdfSpecificationMap[tminMethod.id]
                ? cdfSpecificationMap[tminMethod.id]
                : undefined

            // if the selected method is TMinByShellCourse, check if the component can do RlCustomCrByShellCourse
            if (
              selectedCdfSpec === FdeCdfSpecification.RlByShellCourse &&
              FdeCdfSpecification.RlCustomCrByShellCourse.canCreateCdfData(
                assetModel,
                baseThicknessCDF.tags.component,
                forecastComponentConfig ?? {}
              )
            ) {
              selectedCdfSpec = FdeCdfSpecification.RlCustomCrByShellCourse
            }

            // - - - - - - - - - - - - - - - - - Initialize the Web Worker - - - - - - - - - - - - - - - - -
            // Use a web worker to calculate the data, as this calc can be
            // quite time-intensive, and we don't want to block the main thread

            // Create the web worker, passing in the path to the worker file
            // Note: Defining the web-worker using `new URL(filePath, import.meta.url), {type: 'module'}` is necessary
            //  as it allows us to import/export from within the worker-file.
            // Note: Only JSON-serializable data can be passed to/from the worker.
            const worker = new CalculateCascadedScenariosWorker()

            const calcParams: CalculateCascadeOfScenariosWorkerProps = {
              thicknessValues: baseThicknessValues,
              thicknessUnit: baseThicknessCDF.data.params.measurement_unit.id,
              assetModelJson,
              forecastComponentConfig: forecastComponentConfig ?? {},
              binWidthFt,
              binHeightFt,
              yGeometryStartFt,
              xGeometryStartFt,
              baseInspectionDateString: baseInspectionDate.format(DATE_FORMAT),
              ahpScenarios: assetHealthPlanScenarios,
              componentId: baseThicknessCDF.tags.component,
              useCustomCR: !!(
                componentModel &&
                selectedCdfSpec === FdeCdfSpecification.RlCustomCrByShellCourse
              ),
            }

            // The function that will be called when the web-worker posts a message
            worker.onmessage = async (message) => {
              worker.terminate()
              resolve({
                dataFrames: message.data as AssetHealthDataFrame[],
                calcParams,
              })
            }

            // The function that will be called when the web-worker posts an error
            worker.onerror = async (error) => {
              console.error(error)
              worker.terminate()
              reject()
            }

            // - - - - - - - - - - - - - - - - - - Call the Web Worker - - - - - - - - - - - - - - - - - -
            // Calls the web-worker, evoking the onmessage function defined in the worker file.
            worker.postMessage(calcParams)
          } catch (error) {
            console.error(error)
          }
        })
      ),
    [
      baseThicknessCDF,
      baseThicknessValuesAreLoading,
      assetModelJson,
      baseThicknessValues,
      forecastComponentConfig,
      assetHealthPlanScenarios,
      selectedInspection,
      assetModel,
    ]
  )

  const { data, isLoading: isCalculating } = useSWRImmutable(
    // key
    [
      baseThicknessCDF?.data.params,
      baseThicknessCDF?.generation,
      baseThicknessValuesAreLoading,
      forecastComponentConfig,
      assetHealthPlanScenarios,
      assetModelJson,
    ],
    // function
    calculateDataInWebWorker,
    // options
    {
      // There is no cheap way to compare CDF data to know if it has changed,
      // and SWR will attempt to use stableHash... which involves iterating over
      // all elements in the returned array. Very slow. For more information,
      // see: https://github.com/vercel/swr/issues/1719
      //
      // Instead, check if the params to the RL calculation have changed.
      compare: (a, b) => isEqual(a?.calcParams, b?.calcParams),
    }
  )

  if (data && !hasEverLoaded.current) {
    hasEverLoaded.current = true
  }

  return useMemo(
    () => ({
      dataFrames: data?.dataFrames,
      isLoading: isCalculating || baseThicknessValuesAreLoading,
      hasEverLoaded: hasEverLoaded.current,
    }),
    [data, isCalculating, baseThicknessValuesAreLoading]
  )
}
